import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AIChat from "@/components/AIChat";
import CookieBanner from "@/components/CookieBanner";

// Home stays eager (landing page must render instantly, no flash on first paint).
import Home from "@/pages/Home";

// All other pages are code-split so first-load only fetches the landing chunk.
// This cuts the main bundle by ~70% on a 29-page site and eliminates the
// perceived lag when switching between pages, each route becomes its own
// on-demand chunk that browsers cache after first visit.
//
// The three most-hit follow-up routes (About, Our Story, Services) use
// webpackPrefetch so the browser warm-caches them during idle time after
// Home paints, making subsequent nav feel instant.
const About = lazy(() => import(/* webpackPrefetch: true */ "@/pages/About"));
const OurStory = lazy(() => import(/* webpackPrefetch: true */ "@/pages/OurStory"));
const Services = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Services"));
const Team = lazy(() => import("@/pages/Team"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Industries = lazy(() => import("@/pages/Industries"));
const Resources = lazy(() => import("@/pages/Resources"));
const ResourceDetail = lazy(() => import("@/pages/ResourceDetail"));
const Careers = lazy(() => import("@/pages/Careers"));
const TalentNetwork = lazy(() => import("@/pages/TalentNetwork"));
const Solutions = lazy(() => import("@/pages/Solutions"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const CaseStudies = lazy(() => import("@/pages/CaseStudies"));
const WorkforceSolutions = lazy(() => import("@/pages/WorkforceSolutions"));
const Government = lazy(() => import("@/pages/Government"));
const BusinessAssessment = lazy(() => import("@/pages/BusinessAssessment"));
const CareerReadinessAssessment = lazy(() => import("@/pages/CareerReadinessAssessment"));
const IndustryLanding = lazy(() => import("@/pages/IndustryLanding"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const InterviewInvite = lazy(() => import("@/pages/InterviewInvite"));
const CareerServices = lazy(() => import("@/pages/CareerServices"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Contact = lazy(() => import("@/pages/Contact"));
const Legal = lazy(() => import("@/pages/Legal"));
const Login = lazy(() => import("@/pages/Login"));

import { AuthProvider } from "@/admin/AuthContext";
import AuthCallback from "@/admin/AuthCallback";
import ProtectedRoute from "@/admin/ProtectedRoute";
import AdminLayout from "@/admin/AdminLayout";
// Admin routes are all lazy, they load only when a signed-in user visits /admin,
// keeping them entirely out of the public bundle.
const Dashboard = lazy(() => import("@/admin/Dashboard"));
const Leads = lazy(() => import("@/admin/Leads"));
const Projects = lazy(() => import("@/admin/Projects"));
const ProjectDetail = lazy(() => import("@/admin/ProjectDetail"));
const TeamManagement = lazy(() => import("@/admin/TeamManagement"));
const NotificationSettings = lazy(() => import("@/admin/NotificationSettings"));
const BlogManager = lazy(() => import("@/admin/BlogManager"));
const Subscribers = lazy(() => import("@/admin/Subscribers"));
const Pipeline = lazy(() => import("@/admin/Pipeline"));
const Companies = lazy(() => import("@/admin/Companies"));
const Contracts = lazy(() => import("@/admin/Contracts"));
const Profile = lazy(() => import("@/admin/Profile"));
const Assessment = lazy(() => import("@/pages/Assessment"));
const AssessmentShared = lazy(() => import("@/pages/AssessmentShared"));
const AssessmentSubmissions = lazy(() => import("@/admin/AssessmentSubmissions"));
const AssessmentDetail = lazy(() => import("@/admin/AssessmentDetail"));
const AssessmentConfig = lazy(() => import("@/admin/AssessmentConfig"));
const CareersOpenings = lazy(() => import("@/admin/CareersOpenings"));
const CareersApplications = lazy(() => import("@/admin/CareersApplications"));
const CareerServicesAdmin = lazy(() => import("@/admin/CareerServicesAdmin"));
const CapabilityRequests = lazy(() => import("@/admin/CapabilityRequests"));
const ProgramsTraining = lazy(() => import("@/admin/ProgramsTraining"));

/**
 * RouteFallback, minimal, brand-consistent loading state shown for the
 * ~50-200ms while a lazily-imported chunk downloads. Deliberately
 * unobtrusive so it never feels like a spinner-heavy page load.
 */
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" aria-live="polite" aria-busy="true">
      <div className="w-8 h-8 border-2 border-slate-200 border-t-gold rounded-full animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Reset Lenis (smooth scroll) first if active, otherwise it overrides window.scrollTo
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true, force: true });
    }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, []);
  return null;
}

function AppShell() {
  const location = useLocation();

  // Handle Google OAuth callback BEFORE anything else (synchronous check)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isAppArea = location.pathname.startsWith("/admin") || location.pathname === "/login";

  return (
    <>
      <ScrollToTop />
      {!isAppArea && <SmoothScroll />}
      {!isAppArea && <Navbar />}
      <main>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/team" element={<Team />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/workforce-solutions" element={<WorkforceSolutions />} />
          <Route path="/government" element={<Government />} />
          <Route path="/government-procurement" element={<Government />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/business-assessment" element={<BusinessAssessment />} />
          <Route path="/career-readiness-assessment" element={<CareerReadinessAssessment />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/industries/:slug" element={<IndustryLanding />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/:slug" element={<ResourceDetail />} />
          <Route path="/careers" element={<TalentNetwork />} />
          <Route path="/talent-network" element={<TalentNetwork />} />
          <Route path="/careers/:slug" element={<JobDetail />} />
          <Route path="/career-services" element={<CareerServices />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/interview/:token" element={<InterviewInvite />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/assessment" element={<Assessment />} />
          <Route path="/assessment/shared/:id" element={<AssessmentShared />} />
          <Route path="/privacy" element={<Legal kind="privacy" />} />
          <Route path="/terms" element={<Legal kind="terms" />} />
          <Route path="/accessibility" element={<Legal kind="accessibility" />} />
          <Route path="/login" element={<Login />} />

          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="companies" element={<Companies />} />
            <Route path="contracts" element={<Contracts />} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="blog" element={<BlogManager />} />
            <Route path="subscribers" element={<Subscribers />} />
            <Route path="team" element={<TeamManagement />} />
            <Route path="settings/notifications" element={<NotificationSettings />} />
            <Route path="profile" element={<Profile />} />
            <Route path="assessment/submissions" element={<AssessmentSubmissions />} />
            <Route path="assessment/submissions/:id" element={<AssessmentDetail />} />
            <Route path="assessment/config" element={<AssessmentConfig />} />
            <Route path="careers/openings" element={<CareersOpenings />} />
            <Route path="careers/applications" element={<CareersApplications />} />
            <Route path="career-services" element={<CareerServicesAdmin />} />
            <Route path="capability-requests" element={<CapabilityRequests />} />
            <Route path="programs" element={<ProgramsTraining />} />
          </Route>
          </Routes>
        </Suspense>
      </main>
      {!isAppArea && <Footer />}
      {!isAppArea && <AIChat />}
      {!isAppArea && <CookieBanner />}
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <div className="App font-body text-navy antialiased">
      <BrowserRouter>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
